import type { Identifier } from "ra-core";

import type {
  AdvisorGoal,
  Deal,
  IncentiveProgram,
  IncentiveProgramCode,
  Task,
} from "../types";
import {
  countAccumulatedDirectSales,
  selectDirectSalesInPeriod,
  sumDirectSalesAmountMxn,
} from "./directSales";
import type { Period } from "./periods";
import {
  compareLocalDates,
  getAccumulatedPointPeriod,
  getCurrentMonthPeriod,
  getCurrentQuarterPeriod,
  getLocalDateParts,
  isLocalDateWithinPeriod,
  localDateFromDateOnlyString,
  localDateFromInstant,
  periodMonthKey,
} from "./periods";

/**
 * Almikia Epic E7 (TASK-090): the calculation core shared by "Mi avance"
 * (TASK-092), the Líder BPO dashboard (TASK-093) and the Dirección panel
 * (TASK-094). Returns numbers and states only -- every Spanish label is
 * owned by the UI tickets that consume this module.
 */

/**
 * Minimum all-time accumulated direct sales for the `junior` level
 * (DOMAIN RULE (d), ASSUMPTION: the level model itself arrives with a later
 * epic (E6); the Junior exam is assumed already passed).
 */
const JUNIOR_MIN_ACCUMULATED_SALES = 3;

const MEETING_TASK_TYPE = "meeting";
const PROACTIVE_LEAD_TYPE = "proactivo";

// ---------------------------------------------------------------------------
// Goal progress (DOMAIN RULE (g))
// ---------------------------------------------------------------------------

export type GoalProgress =
  | { status: "no_target" }
  | {
      status: "tracked";
      targetUnits: number | null;
      targetAmountMxn: number | null;
      actualUnits: number;
      actualAmountMxn: number;
    };

/**
 * True when `goal` sets no target at all (no row, or both targets NULL) --
 * DOMAIN RULE (g): shown WITHOUT a target, never as 0% or a shortfall.
 */
function hasNoTarget(goal: AdvisorGoal | undefined): boolean {
  return !goal || (goal.target_units == null && goal.target_amount_mxn == null);
}

/**
 * Sums one target dimension (`target_units` or `target_amount_mxn`) across
 * `goals`, but only over the goals that actually set it -- `null` when NONE
 * of them do, never `0` (DOMAIN RULE (g): a dimension with no target must
 * never read as a 0-target shortfall, even when a sibling dimension is set).
 */
function sumTargetDimension(
  goals: AdvisorGoal[],
  dimension: "target_units" | "target_amount_mxn",
): number | null {
  const withDimension = goals.filter((g) => g[dimension] != null);
  if (withDimension.length === 0) return null;
  return withDimension.reduce((sum, g) => sum + (g[dimension] ?? 0), 0);
}

/**
 * Goal progress for a single advisor's own month (DOMAIN RULE (g)). `goals`
 * may hold any set of rows -- this picks the advisor's own `scope:
 * 'advisor'` row matching `period`'s month.
 */
export function computeAdvisorGoalProgress(
  advisorId: Identifier,
  advisorDeals: Deal[],
  goals: AdvisorGoal[],
  period: Period,
): GoalProgress {
  const monthKey = periodMonthKey(period);
  const goal = goals.find(
    (g) =>
      g.sales_id === advisorId &&
      g.scope === "advisor" &&
      g.period_month === monthKey,
  );
  if (hasNoTarget(goal)) return { status: "no_target" };
  const sales = selectDirectSalesInPeriod(advisorDeals, period);
  return {
    status: "tracked",
    targetUnits: goal?.target_units ?? null,
    targetAmountMxn: goal?.target_amount_mxn ?? null,
    actualUnits: sales.length,
    actualAmountMxn: sumDirectSalesAmountMxn(sales),
  };
}

/**
 * Team goal progress for a Líder BPO: her own `scope: 'team'` row against
 * her own direct sales plus her team's (DOMAIN RULE (g), ASSUMPTION: team
 * progress = the Líder's own sales plus `lider_team_members`'). `teamDeals`
 * must already include the Líder's own deals.
 */
export function computeTeamGoalProgress(
  liderId: Identifier,
  teamDeals: Deal[],
  goals: AdvisorGoal[],
  period: Period,
): GoalProgress {
  const monthKey = periodMonthKey(period);
  const goal = goals.find(
    (g) =>
      g.sales_id === liderId &&
      g.scope === "team" &&
      g.period_month === monthKey,
  );
  if (hasNoTarget(goal)) return { status: "no_target" };
  const sales = selectDirectSalesInPeriod(teamDeals, period);
  return {
    status: "tracked",
    targetUnits: goal?.target_units ?? null,
    targetAmountMxn: goal?.target_amount_mxn ?? null,
    actualUnits: sales.length,
    actualAmountMxn: sumDirectSalesAmountMxn(sales),
  };
}

/**
 * Dirección's agency-wide monthly goal: the sum of every visible
 * advisor-scope goal actually carrying a target for the month (DOMAIN RULE
 * (g), ASSUMPTION: no agency or project goal is modeled); no goal carrying a
 * target means no target at all.
 */
export function computeAgencyGoalProgress(
  agencyDeals: Deal[],
  advisorScopeGoals: AdvisorGoal[],
  period: Period,
): GoalProgress {
  const monthKey = periodMonthKey(period);
  const activeGoals = advisorScopeGoals.filter(
    (g) =>
      g.scope === "advisor" && g.period_month === monthKey && !hasNoTarget(g),
  );
  if (activeGoals.length === 0) return { status: "no_target" };
  const sales = selectDirectSalesInPeriod(agencyDeals, period);
  return {
    status: "tracked",
    targetUnits: sumTargetDimension(activeGoals, "target_units"),
    targetAmountMxn: sumTargetDimension(activeGoals, "target_amount_mxn"),
    actualUnits: sales.length,
    actualAmountMxn: sumDirectSalesAmountMxn(sales),
  };
}

// ---------------------------------------------------------------------------
// Program progress (DOMAIN RULES (d)-(j))
// ---------------------------------------------------------------------------

export type MonthlySaleIncentiveProgress = {
  code: "monthly_sale_incentive";
  period: Period;
  salesCount: number;
  estimatedMxn: number;
};

export type QuarterlyOutstandingSalesProgress = {
  code: "quarterly_outstanding_sales";
  period: Period;
  units: number;
  amountMxn: number;
  /** The highest tier's `rate_pct` reached, or `null` when none is reached yet (DOMAIN RULE (f)). */
  tierReachedRatePct: number | null;
  /** Units still missing to satisfy `min_units`, never negative. */
  unitsMissingToNextTier: number;
  /** MXN still missing to reach the next tier's threshold, never negative; 0 once the top tier is reached. */
  mxnMissingToNextTier: number;
  /** `program.parameters.min_units` -- read from `incentive_programs` DATA, never hardcoded in the UI (bar denominator until it's met). */
  minUnits: number;
  /** The next unreached tier's `threshold_mxn` (bar denominator once `min_units` is met), or `null` once the top tier is already reached. */
  nextTierThresholdMxn: number | null;
};

export type MonthlyProactiveAppointmentsProgress = {
  code: "monthly_proactive_appointments";
  period: Period;
  count: number;
  target: number;
};

export type BestBpoTeamProgress = {
  code: "best_bpo_team";
  period: Period;
  teamUnits: number;
  teamAmountMxn: number;
};

export type GasolineInsuranceProgress = {
  code: "gasoline_insurance";
  /** DOMAIN RULE (i): the earning/redeeming rule is undefined and no data exists -- an explicit empty state, never an invented count. */
  state: "no_data";
};

export type IncentiveProgressModel =
  | MonthlySaleIncentiveProgress
  | QuarterlyOutstandingSalesProgress
  | MonthlyProactiveAppointmentsProgress
  | BestBpoTeamProgress
  | GasolineInsuranceProgress;

/**
 * DOMAIN RULE (j): a program applies to `period` only when its validity
 * window `[valid_from, valid_to]` overlaps `period`'s own window.
 */
export function isProgramApplicableToPeriod(
  program: IncentiveProgram,
  period: Period,
): boolean {
  const validFrom = localDateFromDateOnlyString(program.valid_from);
  if (compareLocalDates(validFrom, period.end) > 0) return false;
  if (program.valid_to == null) return true;
  const validTo = localDateFromDateOnlyString(program.valid_to);
  return compareLocalDates(validTo, period.start) >= 0;
}

/**
 * Picks the ONE version of a program `code` to use for `period`, out of
 * `versions` that all share that `code` (direccion's "Nueva vigencia" can
 * leave two overlapping rows for the same code -- e.g. a new version starting
 * mid-month/mid-quarter -- and both would otherwise pass
 * {@link isProgramApplicableToPeriod}). Prefers the version whose own
 * `[valid_from, valid_to]` window contains TODAY's Mexico City date; when
 * none does (both windows overlap `period` but neither covers today -- an
 * edge case of overlapping historical versions), falls back to the newest
 * `valid_from` among the versions that overlap `period`. Returns `null` when
 * no version overlaps `period` at all.
 */
export function selectApplicableProgramVersion<T extends IncentiveProgram>(
  versions: T[],
  period: Period,
  now: Date = new Date(),
): T | null {
  const overlapping = versions.filter((version) =>
    isProgramApplicableToPeriod(version, period),
  );
  if (overlapping.length === 0) return null;

  const today = getLocalDateParts(now);
  const containingToday = overlapping.find((version) => {
    const validFrom = localDateFromDateOnlyString(version.valid_from);
    if (compareLocalDates(validFrom, today) > 0) return false;
    if (version.valid_to == null) return true;
    return (
      compareLocalDates(localDateFromDateOnlyString(version.valid_to), today) >=
      0
    );
  });
  if (containingToday) return containingToday;

  return overlapping.reduce((newest, version) =>
    compareLocalDates(
      localDateFromDateOnlyString(version.valid_from),
      localDateFromDateOnlyString(newest.valid_from),
    ) > 0
      ? version
      : newest,
  );
}

function computeMonthlySaleIncentive(
  program: Extract<IncentiveProgram, { code: "monthly_sale_incentive" }>,
  advisorDeals: Deal[],
  period: Period,
): MonthlySaleIncentiveProgress | null {
  // DOMAIN RULE (d) (ASSUMPTION): eligible once 3+ direct sales have
  // accumulated all-time -- `min_level: 'junior'` is assumed already passed.
  if (
    countAccumulatedDirectSales(advisorDeals) < JUNIOR_MIN_ACCUMULATED_SALES
  ) {
    return null;
  }
  const sales = selectDirectSalesInPeriod(advisorDeals, period);
  const { rate_pct, cap_mxn_per_sale } = program.parameters;
  const estimatedMxn = sales.reduce(
    (total, sale) =>
      total + Math.min((sale.amountMxn * rate_pct) / 100, cap_mxn_per_sale),
    0,
  );
  return {
    code: "monthly_sale_incentive",
    period,
    salesCount: sales.length,
    estimatedMxn,
  };
}

function computeQuarterlyOutstandingSales(
  program: Extract<IncentiveProgram, { code: "quarterly_outstanding_sales" }>,
  advisorDeals: Deal[],
  period: Period,
): QuarterlyOutstandingSalesProgress {
  const sales = selectDirectSalesInPeriod(advisorDeals, period);
  const units = sales.length;
  const amountMxn = sumDirectSalesAmountMxn(sales);
  const { min_units, tiers } = program.parameters;

  // DOMAIN RULE (f): a tier is reached only when BOTH the unit floor and its
  // own MXN threshold are met; the highest tier reached wins.
  let reachedIndex = -1;
  if (units >= min_units) {
    tiers.forEach((tier, index) => {
      const reached = tier.inclusive
        ? amountMxn >= tier.threshold_mxn
        : amountMxn > tier.threshold_mxn;
      if (reached) reachedIndex = index;
    });
  }
  const nextTier = tiers[reachedIndex + 1];
  return {
    code: "quarterly_outstanding_sales",
    period,
    units,
    amountMxn,
    tierReachedRatePct: reachedIndex >= 0 ? tiers[reachedIndex].rate_pct : null,
    unitsMissingToNextTier: Math.max(0, min_units - units),
    mxnMissingToNextTier: computeMxnMissingToNextTier(nextTier, amountMxn),
    minUnits: min_units,
    nextTierThresholdMxn: nextTier ? nextTier.threshold_mxn : null,
  };
}

/**
 * MXN still missing to reach `nextTier`, never negative. An exclusive
 * (`inclusive: false`) tier is reached only strictly ABOVE its threshold, so
 * exactly at the threshold (the amount neither below nor above it) still
 * reports 1 MXN missing, never 0 -- DOMAIN RULE (f), the "exactly 15M" case.
 */
function computeMxnMissingToNextTier(
  nextTier: { threshold_mxn: number; inclusive: boolean } | undefined,
  amountMxn: number,
): number {
  if (!nextTier) return 0;
  const gap = nextTier.inclusive
    ? nextTier.threshold_mxn - amountMxn
    : nextTier.threshold_mxn - amountMxn + 1;
  return Math.max(0, gap);
}

function computeMonthlyProactiveAppointments(
  program: Extract<
    IncentiveProgram,
    { code: "monthly_proactive_appointments" }
  >,
  advisorId: Identifier,
  advisorDeals: Deal[],
  advisorTasks: Task[],
  period: Period,
): MonthlyProactiveAppointmentsProgress {
  // DOMAIN RULE (c) (ASSUMPTION, no appointment entity yet): a proactive
  // appointment is a done 'meeting' task whose contact belongs to one of
  // this advisor's 'proactivo' leads.
  const proactiveContactIds = new Set(
    advisorDeals
      .filter(
        (deal) =>
          deal.sales_id === advisorId && deal.lead_type === PROACTIVE_LEAD_TYPE,
      )
      .flatMap((deal) => deal.contact_ids),
  );
  const count = advisorTasks.filter(
    (task) =>
      task.sales_id === advisorId &&
      task.type === MEETING_TASK_TYPE &&
      !!task.done_date &&
      proactiveContactIds.has(task.contact_id) &&
      isLocalDateWithinPeriod(localDateFromInstant(task.done_date), period),
  ).length;
  return {
    code: "monthly_proactive_appointments",
    period,
    count,
    target: program.parameters.target_appointments,
  };
}

function computeBestBpoTeam(
  teamDeals: Deal[],
  period: Period,
): BestBpoTeamProgress {
  const sales = selectDirectSalesInPeriod(teamDeals, period);
  return {
    code: "best_bpo_team",
    period,
    teamUnits: sales.length,
    teamAmountMxn: sumDirectSalesAmountMxn(sales),
  };
}

function computeGasolineInsurance(): GasolineInsuranceProgress {
  return { code: "gasoline_insurance", state: "no_data" };
}

export type AdvisorProgramContext = {
  advisorId: Identifier;
  /** This advisor's own deals -- any stage, any period; used for both period-scoped stats and the all-time accumulated-sales eligibility gate. */
  advisorDeals: Deal[];
  /** This advisor's own tasks -- any type/date; filtered internally to each program's period. */
  advisorTasks: Task[];
  /** Deals of the advisor's own team (her own deals plus her team members'), required only for `best_bpo_team`; omit when the advisor has no team. */
  teamDeals?: Deal[];
  now?: Date;
};

/**
 * Groups `programs` by `code`, keeping insertion order of each code's first
 * occurrence -- the fan-in point for {@link selectApplicableProgramVersion}
 * so a "Nueva vigencia" (direccion editing a program) never yields two rows
 * for the same code.
 */
function groupProgramsByCode(
  programs: IncentiveProgram[],
): Map<IncentiveProgramCode, IncentiveProgram[]> {
  const byCode = new Map<IncentiveProgramCode, IncentiveProgram[]>();
  for (const program of programs) {
    const versions = byCode.get(program.code) ?? [];
    versions.push(program);
    byCode.set(program.code, versions);
  }
  return byCode;
}

/**
 * One progress model per program CODE the advisor is both eligible for and
 * whose validity window covers the program's own period (DOMAIN RULES
 * (d)-(j)); a program that does not apply, or the advisor is not eligible
 * for, is simply absent from the result -- never a zeroed-out model. When
 * "Nueva vigencia" leaves multiple overlapping rows for the same `code` (a
 * direccion edit starting mid-period), exactly one version is ever chosen
 * per code -- see {@link selectApplicableProgramVersion}.
 */
export function computeAdvisorProgramProgress(
  programs: IncentiveProgram[],
  context: AdvisorProgramContext,
): IncentiveProgressModel[] {
  const now = context.now ?? new Date();
  const models: IncentiveProgressModel[] = [];

  for (const [, versions] of groupProgramsByCode(programs)) {
    const period =
      versions[0].period === "month"
        ? getCurrentMonthPeriod(now)
        : versions[0].period === "quarter"
          ? getCurrentQuarterPeriod(now)
          : getAccumulatedPointPeriod(now);
    const program = selectApplicableProgramVersion(versions, period, now);
    if (!program) continue;

    switch (program.code) {
      case "monthly_sale_incentive": {
        const model = computeMonthlySaleIncentive(
          program,
          context.advisorDeals,
          period,
        );
        if (model) models.push(model);
        break;
      }
      case "quarterly_outstanding_sales":
        models.push(
          computeQuarterlyOutstandingSales(
            program,
            context.advisorDeals,
            period,
          ),
        );
        break;
      case "monthly_proactive_appointments":
        models.push(
          computeMonthlyProactiveAppointments(
            program,
            context.advisorId,
            context.advisorDeals,
            context.advisorTasks,
            period,
          ),
        );
        break;
      case "best_bpo_team":
        if (context.teamDeals) {
          models.push(computeBestBpoTeam(context.teamDeals, period));
        }
        break;
      case "gasoline_insurance":
        models.push(computeGasolineInsurance());
        break;
    }
  }

  return models;
}
